// What a suspended or closed tenant's members see (spec 15 §3.2 and §4 S20;
// D-495, D-627). lib/tenant-suspension.ts holds what the server said; this
// file draws it.
//
//   * S20, the lock screen: a full page in place of the office or the floor
//     while the tenant is locked (an immediate suspension, or a grace window
//     that ran out). It names the category (billing, or "Assessly paused it"),
//     never the specific reason, says the data is kept, and offers sign-out.
//   * The closed page: a purged tenant's host answers 410, and the member
//     reads that the workspace was closed.
//   * The grace banner: red, above every office and floor screen while the
//     grace window is open, with its end in the tenant's own time zone.
//
// The billing contact is Assessly itself: invoices are manual until module 19
// (D-024), so there is no billing address to link to, and the page names none
// rather than inventing one.

import { useEffect, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { Banner } from "../components/toast";
import { formatTimestamp } from "../lib/datetime";
import { joinSentences } from "../lib/sentences";
import { readProfileOnce } from "../lib/profile-locale";
import {
  clearSuspension,
  noteSuspension,
  suspensionFromProfile,
  tenantSuspension,
  type SuspensionCategory,
  type TenantSuspension,
} from "../lib/tenant-suspension";

/** S20: the tenant is locked. */
export function SuspendedScreen({
  category,
  signOut,
}: {
  category: SuspensionCategory;
  /** The shell's own sign-out, which also hands a floor device over. */
  signOut: ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <div className="app">
      <header className="app-header">
        <span className="app-title">{t("app.title")}</span>
      </header>
      <main className="app-main">
        <section
          className="auth-card"
          data-testid="tenant-suspended"
          data-category={category}
          role="alert"
        >
          <h1 className="auth-title">{t("auth.suspension.lockedTitle")}</h1>
          <p className="auth-lead">
            {category === "billing"
              ? t("auth.suspension.lockedBilling")
              : t("auth.suspension.lockedOther")}
          </p>
          <p className="auth-lead">{t("auth.suspension.dataKept")}</p>
          <div className="auth-actions">{signOut}</div>
        </section>
      </main>
    </div>
  );
}

/** The workspace was purged (410 TENANT_PURGED). */
export function WorkspaceClosedScreen() {
  const { t } = useTranslation();
  return (
    <div className="app">
      <header className="app-header">
        <span className="app-title">{t("app.title")}</span>
      </header>
      <main className="app-main">
        <section className="auth-card" data-testid="tenant-closed" role="alert">
          <h1 className="auth-title">{t("auth.suspension.closedTitle")}</h1>
          <p className="auth-lead">{t("auth.suspension.closedBody")}</p>
        </section>
      </main>
    </div>
  );
}

/** The sentence the grace banner and a refused submit button carry. */
export function graceSentence(
  state: Extract<TenantSuspension, { kind: "grace" }>,
  t: TFunction,
  locale: string,
): string {
  const date = formatTimestamp(state.graceEndsAt, locale, state.timezone ?? null);
  return state.category === "billing"
    ? t("auth.suspension.graceBilling", { date })
    : t("auth.suspension.graceOther", { date });
}

/** The red read-only banner above every office and floor screen in grace. */
export function GraceBanner({
  state,
  floor = false,
}: {
  state: Extract<TenantSuspension, { kind: "grace" }>;
  /** On the floor the banner also says the queue is holding the work. */
  floor?: boolean;
}) {
  const { t, i18n } = useTranslation();
  const sentences = [graceSentence(state, t, i18n.language)];
  if (floor) sentences.push(t("auth.suspension.floorPaused"));
  return (
    <Banner kind="bad" data-testid="tenant-grace-banner" data-category={state.category}>
      {joinSentences(sentences, i18n.language)}
    </Banner>
  );
}

/**
 * Read the signed-in profile's grace window into the store (D-627 ruling 3),
 * so the banner is up before the first refused write. A profile that names
 * no window ends a grace record a 403 left behind.
 */
export function useSuspensionFromProfile(): void {
  useEffect(() => {
    let live = true;
    void readProfileOnce().then((profile) => {
      if (!live || profile === undefined) return;
      const next = suspensionFromProfile(profile);
      if (next.kind === "grace") noteSuspension(next);
      else if (tenantSuspension().kind === "grace") clearSuspension();
    });
    return () => {
      live = false;
    };
  }, []);
}
